import * as React from 'react';
import Cropper from 'react-easy-crop';
import {Button, Flex, Slider, SyncIcon, Text} from "@fluentui/react-northstar";
import {Area, Point} from "react-easy-crop/types";
import languageObject from "../../translations";

/**
 * Image Cropper props
 */
interface IImageCropperProps {
    imageSrc: string,
    imageWidth: number,
    imageHeight: number,
    innerRef?: any
}

/**
 * Image Cropper state
 */
interface IImageCropperState {
    crop: { x: number, y: number },
    zoom: number,
    rotation: number,
    aspect: number,
    selectedAspect: { x: number, y: number },
    invertAspect: boolean,
    croppedAreaPixels: Area | undefined,
}

/**
 * Default state
 */
const defaultState: IImageCropperState = {
    crop: {x: 0, y: 0},
    zoom: 1,
    rotation: 0,
    aspect: 1,
    selectedAspect: {x: 1, y: 1},
    invertAspect: false,
    croppedAreaPixels: undefined
};

/**
 * Image Cropper
 */
class ImageCropper extends React.Component<IImageCropperProps, IImageCropperState> {
    /**
     * Constructor
     * @param props
     */
    constructor(props: IImageCropperProps) {
        super(props);
        this.state = defaultState;
    }

    /**
     * Component initialization
     */
    componentDidMount() {
        // Fix sync icon
        setTimeout(() => {
            const mySVG = document.getElementById('sync-icon')?.getElementsByTagName('svg')[0];
            mySVG?.setAttribute("viewBox", "2 0 16 20");
        }, 100);
        if (!!this.props.innerRef)
            this.props.innerRef.current = this;
    }

    componentWillUnmount() {
        if (!!this.props.innerRef)
            this.props.innerRef.current = null;
    }

    /**
     * Reset state
     */
    resetState = () => {
        this.setState(defaultState);
    }

    /**
     * Create an image from base64 data url
     * @param url image url
     */
    createImage = (url: string) => {
        return new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = (error: any) => reject(error);
            image.src = url;
        })
    }

    /**
     * Get cropped image
     */
    getCroppedImg = async () => {
        if (!this.state.croppedAreaPixels) {
            console.error("Cropped area not defined");
            return;
        }
        const image = await this.createImage(this.props.imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const maxSize = Math.max(image.width, image.height);
        const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

        // Set each dimensions to double largest dimension to allow for a safe area for the
        // image to rotate in without being clipped by canvas context
        canvas.width = safeArea
        canvas.height = safeArea

        // Translate canvas context to a central location on image to allow rotating around the center.
        ctx.translate(safeArea / 2, safeArea / 2)
        ctx.rotate((this.state.rotation * Math.PI) / 180)
        ctx.translate(-safeArea / 2, -safeArea / 2)

        // Draw rotated image and store data.
        ctx.drawImage(
            image,
            safeArea / 2 - image.width * 0.5,
            safeArea / 2 - image.height * 0.5
        )
        const data = ctx.getImageData(0, 0, safeArea, safeArea)

        // Set canvas width to final desired crop size - this will clear existing context
        canvas.width = this.state.croppedAreaPixels.width
        canvas.height = this.state.croppedAreaPixels.height

        // Paste generated rotate image with correct offsets for x,y crop values
        ctx.putImageData(
            data,
            Math.round(0 - safeArea / 2 + image.width * 0.5 - this.state.croppedAreaPixels.x),
            Math.round(0 - safeArea / 2 + image.height * 0.5 - this.state.croppedAreaPixels.y)
        )

        // File content type
        let contentType = this.props.imageSrc.split("data:")[1].split(";base64,")[0];
        if (contentType !== "image/png") contentType = "image/jpeg";

        // Return content
        return canvas.toDataURL(contentType);
    }

    /**
     * When crop has changed
     * @param location
     */
    onCropChange = (location: Point) => {
        this.setState({crop: location})
    }

    /**
     * When crop is done
     * @param croppedArea
     * @param croppedAreaPixels
     */
    onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
        this.setState({croppedAreaPixels});
    }

    /**
     * When aspect ratio has changed
     * @param selectedAspect aspect ratio
     */
    onAspectRatioChange = (selectedAspect: { x: number, y: number }) => {
        const aspect = !this.state.invertAspect ?
            selectedAspect.x / selectedAspect.y
            :
            selectedAspect.y / selectedAspect.x;
        this.setState({selectedAspect, aspect});
    }

    /**
     * Flip aspect ratio
     */
    onFlipAspectRatio = () => {
        this.setState({
            invertAspect: !this.state.invertAspect,
            aspect: !this.state.invertAspect ?
                this.state.selectedAspect.y / this.state.selectedAspect.x
                :
                this.state.selectedAspect.x / this.state.selectedAspect.y
        });
    }

    /**
     * When zoom has changed
     * @param zoom zoom
     */
    onZoomChange = (zoom: number) => {
        this.setState({zoom});
    }

    /**
     * When rotation has changed
     * @param rotation rotation
     */
    onRotationChange = (rotation: number) => {
        this.setState({rotation});
    }

    render() {
        return (
            <Flex column>
                <Cropper
                    classes={{
                        containerClassName: "crop_container",
                        cropAreaClassName: "crop_area",
                        mediaClassName: "crop_media"
                    }}
                    image={this.props.imageSrc}
                    crop={this.state.crop}
                    zoom={this.state.zoom}
                    rotation={this.state.rotation}
                    aspect={this.state.aspect}
                    onCropChange={this.onCropChange}
                    onCropComplete={this.onCropComplete}
                    onZoomChange={this.onZoomChange}
                />
                <Flex gap={"gap.medium"} styles={{marginTop: "15px"}}>
                    {/* Labels */}
                    <Flex column vAlign={"center"} gap={"gap.small"}>
                        <Text styles={{height: "25px", whiteSpace: "nowrap"}}
                              content={languageObject.translate("Zoom")}/>
                        <Text styles={{height: "25px", whiteSpace: "nowrap"}}
                              content={languageObject.translate("Rotation")}/>
                        <Text styles={{height: "25px", whiteSpace: "nowrap"}}
                              content={languageObject.translate("AspectRatio")}/>
                    </Flex>
                    <Flex fill column vAlign={"center"} gap={"gap.small"}>
                        {/* Zoom slider */}
                        <Slider
                            styles={{height: "25px"}}
                            fluid
                            value={this.state.zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e, zoom) => this.onZoomChange(parseFloat(zoom?.value as string))}
                        />
                        {/* Rotation slider */}
                        <Slider
                            styles={{height: "25px"}}
                            fluid
                            value={this.state.rotation}
                            min={0}
                            max={360}
                            step={10}
                            onChange={(e, rotation) => this.onRotationChange(parseInt(rotation?.value as string))}
                        />
                        {/* Aspect ratios */}
                        <Flex fill gap={"gap.medium"} hAlign={"stretch"} styles={{height: "25px"}}>
                            <Button size={"small"} icon={<SyncIcon id="sync-icon" size={"large"}/>}
                                    iconOnly text onClick={this.onFlipAspectRatio}/>
                            {this.renderAspectRatioBtn(1, 1)}
                            {this.renderAspectRatioBtn(3, 2)}
                            {this.renderAspectRatioBtn(4, 3)}
                            {this.renderAspectRatioBtn(5, 4)}
                            {this.renderAspectRatioBtn(16, 9)}
                            {this.renderAspectRatioBtn(16, 10)}
                            {this.renderAspectRatioBtn(21, 9)}
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
        )
    }

    /**
     * Render aspect ratio button
     * @param x x value
     * @param y y value
     */
    renderAspectRatioBtn = (x: number, y: number) => {
        const isSelected = this.state.selectedAspect.x === x &&
            this.state.selectedAspect.y === y;
        return (
            <Button primary={isSelected} size={"small"} styles={{minWidth: "auto"}} content={x + ":" + y}
                    onClick={() => this.onAspectRatioChange({x, y})}/>
        )
    }
}

export default ImageCropper